import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
} from '@mui/material';

interface InlineResultTableProps {
  /** Column headers */
  columns: string[];
  /** Row data — each row is an array of values matching the columns */
  rows: any[][];
  /** Optional title above the table */
  title?: string;
  /** Maximum rows to display before truncation notice */
  maxRows?: number;
  /** Total rows (server-side), shown when truncated */
  totalRows?: number;
}

const InlineResultTable: React.FC<InlineResultTableProps> = ({
  columns,
  rows,
  title,
  maxRows = 50,
  totalRows,
}) => {
  const displayRows = rows.slice(0, maxRows);
  const isTruncated = rows.length > maxRows || (totalRows != null && totalRows > rows.length);

  return (
    <Box sx={{ my: 1 }}>
      {title && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {title}
        </Typography>
      )}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 300, overflow: 'auto' }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600, bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                  {rowIdx + 1}
                </TableCell>
                {row.map((cell, cellIdx) => (
                  <TableCell key={cellIdx} sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cell == null ? (
                      <Chip label="null" size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 18 }} />
                    ) : (
                      String(cell)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isTruncated && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Showing {displayRows.length} of {totalRows ?? rows.length} rows
        </Typography>
      )}
    </Box>
  );
};

export default InlineResultTable;
